import React from 'react'
import type { NextPage, GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { Box, useColorModeValue, Text, Link } from '@chakra-ui/react'
import { InfoFrame } from '@/components/InfoFrame'
import { PageHero } from '@/components/PageHero'
import { Highlights } from '@/components/Highlights'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { SectionWithCards } from '@/components/SectionWithCards'
import { ShopNowCard } from '@/components/ShopNowCard'
import { PaymentIcon } from '@/components/icons/PaymentIcon'
import { CardIcon } from '@/components/icons/CardIcon'
import { BalanceIcon } from '@/components/icons/BalanceIcon'
import { SUPPORT_PREPAID_CREDITS_URL } from '@/constants'
import { HighlightItem } from '@/types/highlights.interface'
import SeoHead from '@/components/seo-head'
import type { CardData } from '@/components/SectionWithCards'

const PrepaidCreditsPage: NextPage = () => {
  const t = useTranslations()
  const cardBg = useColorModeValue('white', 'black')
  const cardTitleColor = useColorModeValue('black', 'white')
  const cardDescriptionColor = useColorModeValue(
    'blackAlpha.800',
    'whiteAlpha.800'
  )
  const cardBorderColor = useColorModeValue('blackAlpha.500', 'whiteAlpha.500')
  const bg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100')

  const items: HighlightItem[] = [
    {
      icon: CardIcon,
      title: t('dashboard.billing.prepaid-credits.purchase-credits'),
      description: t('prepaid-credits.credits-in-advance'),
    },
    {
      icon: BalanceIcon,
      title: t('prepaid-credits.monitor-balance'),
      description: t('prepaid-credits.track-available-credits'),
    },
    {
      icon: PaymentIcon,
      title: t('prepaid-credits.apply-credits-flexibly'),
      description: t('prepaid-credits.cover-monthly-hosting'),
    },
  ]

  const savingsFlexibility: CardData[] = [
    {
      title: t('prepaid-credits.discounted-rates'),
      description: t('prepaid-credits.acquire-credits'),
      iconImage: { url: '/img/prepaid_credits_banner.png' },
    },
    {
      title: t('prepaid-credits.effortless-invoice-management'),
      description: t('prepaid-credits.accumulated-credits'),
      iconImage: { url: '/img/payment-plan-page.png' },
      objectPosition: 'left',
    },
    {
      title: t('prepaid-credits.hardware-purchase-flexibility'),
      description: t('prepaid-credits.leverage-credit-balance'),
      iconImage: { url: '/img/hardware-purchase.png' },
      iconImageMobile: { url: '/img/hardware-purchase-mobile.png' },
    },
    {
      title: t('prepaid-credits.transparent-transaction-details'),
      description: t('prepaid-credits.monitor-credits'),
      iconImage: { url: '/img/balance-page.png' },
      objectPosition: 'left',
    },
  ]

  return (
    <>
      <SeoHead
        title={t('prepaid-credits.seo.title')}
        breadcrumbTitle={t('prepaid-credits.seo.breadcrumb')}
        description={t('prepaid-credits.seo.description')}
      />
      <PageHero
        title={t('prepaid-credits.maximize-savings')}
        subTitle={t('agreements.prepaid-credits')}
        description={t('prepaid-credits.purchase-credits')}
        image="img/prepaid-credits-hero.png"
        mobileImage="img/prepaid-credits-hero.png"
        anchor="what-are-prepaid-credits"
      />
      <Box id="what-are-prepaid-credits">
        <InfoFrame
          title={t('prepaid-credits.what-are-prepaid-credits')}
          description={t.rich('prepaid-credits.unparalleled-savings', {
            br: () => <br />,
          })}
        />
      </Box>
      <Highlights
        title={t('prepaid-credits.buy-prepaid-credits')}
        items={items}
        columns={3}
      />
      <ShopNowCard />
      <Box bg={bg}>
        <ContainerBase
          px={{ base: '32px', md: 4 }}
          py={{ base: '42px', md: '100px' }}
        >
          <SectionWithCards
            title={t('prepaid-credits.unlock-savings')}
            subtitle={t('prepaid-credits.save-up-hosting')}
            cards={savingsFlexibility}
            cardImageOverlay={false}
            cardImageBg="gray.890"
            cardContentPy={8}
            cardBg={cardBg}
            cardTitleColor={cardTitleColor}
            cardDescriptionColor={cardDescriptionColor}
            cardBorderColor={cardBorderColor}
            cardImageHeight="252px"
            alignStart
            columns={2}
          />
          <Text
            variant="spaceGrotesk"
            fontSize={{ base: '16px', md: '24px' }}
            fontWeight="300"
          >
            {t.rich('prepaid-credits.prepaid-credits-faq', {
              link: (children) => (
                <Link
                  variant="black"
                  textDecoration="underline"
                  href={SUPPORT_PREPAID_CREDITS_URL}
                >
                  {children}
                </Link>
              ),
            })}
          </Text>
        </ContainerBase>
      </Box>
    </>
  )
}

export async function getStaticProps({ locale }: GetServerSidePropsContext) {
  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
    },
  }
}

export default PrepaidCreditsPage
